// ===================================================
// ФАЙЛ: frontend/src/constants/resourceMeta.js
// Единая конфигурация цветов, иконок и стилей для всех типов ресурсов
// Загружается из API с fallback на дефолтные значения
// Используется глобально во всех компонентах
// ===================================================

import React from 'react';
import {
  BadgeCheck,
  Clock,
  Code,
  Contact,
  Globe,
  Grid,
  Home,
  Landmark,
  Server,
  ShoppingBag,
  ShoppingCart,
  Store,
  Truck,
  User,
  Users,
  Webhook,
  Zap,
} from 'lucide-react';
import WayGptIcon from '../components/Common/WayGptIcon';

// ✅ КРИТИЧЕСКОЕ ИСПРАВЛЕНИЕ: Ленивый импорт api для предотвращения циклических зависимостей
// Импортируем api только когда он действительно нужен
let apiInstance = null;
const getApi = () => {
  if (apiInstance) {
    return apiInstance;
  }

  try {
    // ✅ Используем динамический импорт через require для избежания проблем с циклическими зависимостями
    // В production сборке webpack обработает это корректно
    if (typeof require !== 'undefined') {
      const axiosModule = require('../utils/axios');
      apiInstance = axiosModule.default || axiosModule;
      if (apiInstance && typeof apiInstance.get === 'function') {
        return apiInstance;
      }
    }
  } catch (error) {
    // Если require не доступен или произошла ошибка - пробуем через window
    console.warn('⚠️ Не удалось загрузить api через require:', error.message);
  }

  // ✅ Fallback: пробуем найти api в глобальной области (если был экспортирован)
  if (typeof window !== 'undefined' && window.__MARKBASE_API__) {
    apiInstance = window.__MARKBASE_API__;
    return apiInstance;
  }

  return null;
};

/** Фоновая подгрузка мета/темы — без toast; в dev фиксируем неожиданный reject. */
function reportResourceMetaSideEffectFailure(label, err) {
  if (process.env.NODE_ENV !== 'development') return;
  console.warn(`[resourceMeta] ${label}:`, err?.message || err);
}

// Дефолтная конфигурация для всех типов ресурсов (fallback)
// ВСЕ ИКОНКИ - outline стиль, закругленные, минималистичные
const DEFAULT_RESOURCE_META = {
  products: {
    icon: null, // Больше не используем emoji
    iconComponent: Grid,
    iconName: 'Grid', // Название для сохранения в БД (lucide-react)
    title: 'Каталог',
    color: '#0284C7'
  },
  warehouses: {
    icon: null,
    iconComponent: Landmark,
    iconName: 'Landmark',
    title: 'Склады',
    color: '#F59E0B'
  },
  suppliers: {
    icon: null,
    iconComponent: Store,
    iconName: 'Store',
    title: 'Поставщики',
    color: '#722ed1'
  },
  supplier_offers: {
    icon: null,
    iconComponent: Store,
    iconName: 'Store',
    title: 'Offers поставщиков',
    color: '#722ed1'
  },
  import_jobs_per_day: {
    icon: null,
    iconComponent: Truck,
    iconName: 'Truck',
    title: 'Импорты / день',
    color: '#0ea5e9'
  },
  import_jobs: {
    icon: null,
    iconComponent: Truck,
    iconName: 'Truck',
    title: 'Импорты / день',
    color: '#0ea5e9'
  },
  marketplaces: {
    icon: null,
    iconComponent: Globe,
    iconName: 'Globe',
    title: 'Маркетплейсы',
    color: '#10B981'
  },
  users: {
    icon: null,
    iconComponent: Users,
    iconName: 'Users',
    title: 'Пользователи',
    color: '#eb2f96'
  },
  cron_jobs: {
    icon: null,
    iconComponent: Zap,
    iconName: 'Zap',
    title: 'Автоматизация',
    color: '#13c2c2'
  },
  api_requests: {
    icon: null,
    iconComponent: Webhook,
    iconName: 'Webhook',
    title: 'API запросы',
    color: '#EF4444'
  },
  api_integrations: {
    icon: null,
    iconComponent: Webhook,
    iconName: 'Webhook',
    title: 'API интеграции',
    color: '#EF4444'
  },
  rest_api_integration: {
    icon: null,
    iconComponent: Code,
    iconName: 'Code',
    title: 'REST интеграции',
    color: '#2f54eb'
  },
  shop_sites: {
    icon: null,
    iconComponent: Store,
    iconName: 'Store',
    title: 'Магазины',
    color: '#08979c'
  },
  crm: {
    icon: null,
    iconComponent: Contact,
    iconName: 'Contact',
    title: 'CRM',
    color: '#531dab'
  },
  crm_contacts: {
    icon: null,
    iconComponent: Contact,
    iconName: 'Contact',
    title: 'CRM контакты',
    color: '#531dab'
  },
  hrm: {
    icon: null,
    iconComponent: BadgeCheck,
    iconName: 'BadgeCheck',
    title: 'HRM',
    color: '#c41d7f'
  },
  hrm_employees: {
    icon: null,
    iconComponent: BadgeCheck,
    iconName: 'BadgeCheck',
    title: 'Сотрудники',
    color: '#c41d7f'
  },
  orders: {
    icon: null,
    iconComponent: ShoppingCart,
    iconName: 'ShoppingCart',
    title: 'Заказы',
    color: '#d46b08'
  },
  orders_monthly: {
    icon: null,
    iconComponent: ShoppingCart,
    iconName: 'ShoppingCart',
    title: 'Заказы (месяц)',
    color: '#d46b08'
  },
  delivery: {
    icon: null,
    iconComponent: Truck,
    iconName: 'Truck',
    title: 'Доставка',
    color: '#237804'
  },
  delivery_shipments_monthly: {
    icon: null,
    iconComponent: Truck,
    iconName: 'Truck',
    title: 'Отгрузки (месяц)',
    color: '#237804'
  },
  waygpt: {
    icon: null,
    iconComponent: WayGptIcon,
    iconName: 'WayGptIcon',
    title: 'ВЭЙГПТ AI',
    color: '#7cb305'
  },
  waygen_requests_monthly: {
    icon: null,
    iconComponent: WayGptIcon,
    iconName: 'WayGptIcon',
    title: 'AI запросы (месяц)',
    color: '#7cb305'
  },
  plan_fee: {
    icon: null,
    iconComponent: Server,
    iconName: 'Server',
    title: 'Тариф (абонплата)',
    color: '#1d39c4'
  },
};

// Кэш загруженных стилей
let cachedStyles = null;
let cachedCompanyStyles = null;
let loadingPromise = null;
let companyStylesLoadingPromise = null;

// Маппинг iconName (lucide-react) → компонент для данных из API/БД
const ICON_COMPONENTS_MAP = {
  Grid,
  Landmark,
  Store,
  Globe,
  Users,
  Zap,
  Webhook,
  User,
  Truck,
  ShoppingCart,
  Contact,
  BadgeCheck,
  Server,
  WayGptIcon,
  Code,
  ShoppingBag,
  Home,
  Clock,
};

const DEFAULT_ICON_COMPONENTS = {
  products: Grid,
  warehouses: Landmark,
  suppliers: Store,
  marketplaces: Globe,
  users: Users,
  cron_jobs: Zap,
  api_requests: Webhook,
  api_integrations: Webhook,
};

/**
 * Загрузка стилей из API.
 * С компанией: один запрос → effective (платформа + overlay), без копирования стилей в БД компании.
 * Без компании: публичные платформенные стили.
 */
const loadStylesFromAPI = async (companyId = null) => {
  try {
    const api = getApi();
    if (!api || typeof api.get !== 'function') {
      console.warn('⚠️ API client not initialized, using default styles');
      cachedStyles = DEFAULT_RESOURCE_META;
      return DEFAULT_RESOURCE_META;
    }

    let apiStylesLayer = null;

    if (companyId) {
      try {
        const companyResponse = await api.get(`/api/settings/companies/${companyId}/resource-styles`);
        if (companyResponse.data?.success && companyResponse.data.styles) {
          // Backend отдаёт effective_styles в поле styles (наследование платформы).
          apiStylesLayer = companyResponse.data.styles;
          cachedCompanyStyles = companyResponse.data.company_styles || {};
        }
      } catch (e) {
        // fallback ниже на public
      }
    }

    if (!apiStylesLayer) {
      const globalResponse = await api.get('/api/admin/public/resource-styles');
      if (globalResponse.data?.success && globalResponse.data.styles) {
        apiStylesLayer = globalResponse.data.styles;
      }
    }

    let mergedStyles = {};
    if (apiStylesLayer && typeof apiStylesLayer === 'object') {
      Object.keys(DEFAULT_RESOURCE_META).forEach((resourceType) => {
        const apiStyle = apiStylesLayer[resourceType] || {};
        const iconType = apiStyle.iconType || 'component';

        let iconComponent = DEFAULT_ICON_COMPONENTS[resourceType];
        if (iconType === 'component' && apiStyle.iconName) {
          iconComponent = ICON_COMPONENTS_MAP[apiStyle.iconName] || iconComponent;
        } else if (iconType === 'svg' || iconType === 'url') {
          iconComponent = null;
        }

        mergedStyles[resourceType] = {
          ...DEFAULT_RESOURCE_META[resourceType],
          ...apiStyle,
          iconType,
          iconComponent,
        };
      });
    } else {
      mergedStyles = DEFAULT_RESOURCE_META;
    }

    cachedStyles = mergedStyles;
    return mergedStyles;
  } catch (error) {
    if (error?.response?.status !== 404) {
      console.warn('Не удалось загрузить стили из API, используем дефолтные:', error);
    }
  }

  cachedStyles = DEFAULT_RESOURCE_META;
  return DEFAULT_RESOURCE_META;
};

/**
 * Загрузка стилей компании
 */
export const loadCompanyStylesFromAPI = async (companyId) => {
  if (!companyId) return null;

  try {
    // ✅ КРИТИЧЕСКОЕ ИСПРАВЛЕНИЕ: Используем ленивый импорт api
    const api = getApi();
    if (!api || typeof api.get !== 'function') {
      console.warn('⚠️ API client not initialized, skipping company styles');
      return null;
    }

    if (companyStylesLoadingPromise) {
      return companyStylesLoadingPromise;
    }

    companyStylesLoadingPromise = api.get(`/api/settings/companies/${companyId}/resource-styles`)
      .then(response => {
        if (response.data.success && response.data.styles) {
          cachedCompanyStyles = response.data.styles;
          return response.data.styles;
        }
        return null;
      })
      .finally(() => {
        companyStylesLoadingPromise = null;
      });

    return companyStylesLoadingPromise;
  } catch (error) {
    console.warn('Не удалось загрузить стили компании:', error);
    return null;
  }
};

/**
 * Инициализация стилей (загрузка из API при первом обращении)
 * @param {number|null} companyId - ID компании для загрузки стилей компании
 */
const initStyles = async (companyId = null) => {
  if (cachedStyles && (!companyId || cachedCompanyStyles)) {
    return cachedStyles;
  }

  if (loadingPromise) {
    return loadingPromise;
  }

  loadingPromise = loadStylesFromAPI(companyId).finally(() => {
    loadingPromise = null;
  });

  return loadingPromise;
};

/**
 * Получить мета-информацию о ресурсе
 * Загружает стили из API при первом обращении
 * @param {string} resourceType - Тип ресурса
 * @param {number|null} companyId - ID компании (для применения стилей компании, опционально)
 * @returns {Object} Мета-информация или значения по умолчанию
 */
export const getResourceMeta = (resourceType, companyId = null) => {
  // Если стили еще не загружены или нужно загрузить стили компании, инициализируем загрузку
  if (!cachedStyles || (companyId && !cachedCompanyStyles)) {
    // Инициализируем асинхронно в фоне
    initStyles(companyId).catch((e) => reportResourceMetaSideEffectFailure('initStyles', e));

    // Возвращаем дефолтное значение для немедленного использования
    return DEFAULT_RESOURCE_META[resourceType] || {
      icon: null,
      iconComponent: DEFAULT_ICON_COMPONENTS[resourceType] || null,
      iconName: null,
      iconType: 'component',
      title: resourceType,
      color: '#595959'
    };
  }

  return cachedStyles[resourceType] || {
    icon: null,
    iconComponent: DEFAULT_ICON_COMPONENTS[resourceType] || null,
    iconName: null,
    iconType: 'component',
    title: resourceType,
    color: '#595959'
  };
};

/**
 * Универсальная функция для рендеринга иконки ресурса с одинаковым размером
 * Поддерживает component, url и svg типы иконок
 * Требует импорт React в компоненте, где используется
 * @param {string} resourceType - Тип ресурса
 * @param {Object} options - Опции рендеринга
 * @param {number} options.size - Размер иконки в пикселях (по умолчанию 16)
 * @param {string} options.color - Цвет иконки (по умолчанию из мета)
 * @param {number|null} options.companyId - ID компании
 * @param {string} options.className - CSS класс
 * @param {Object} options.style - Дополнительные стили
 * @returns {React.ReactElement|null} React элемент иконки или null
 */
export const renderResourceIcon = (resourceType, options = {}) => {
  const {
    size = 16,
    color = null,
    companyId = null,
    className = '',
    style = {}
  } = options;

  const meta = getResourceMeta(resourceType, companyId);
  const iconType = meta.iconType || 'component';
  const iconColor = color || meta.color || '#595959';

  // Стандартный размер для всех иконок
  const iconStyle = {
    width: size,
    height: size,
    fontSize: size,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: iconColor,
    ...style
  };

  if (iconType === 'svg' && meta.iconSvg) {
    // SVG иконка из кода
    return React.createElement(
      'span',
      {
        className: className,
        dangerouslySetInnerHTML: { __html: meta.iconSvg },
        style: iconStyle
      }
    );
  } else if (iconType === 'url' && meta.iconUrl) {
    // URL иконка (изображение)
    return React.createElement(
      'img',
      {
        src: meta.iconUrl,
        alt: '',
        className: className,
        style: {
          ...iconStyle,
          objectFit: 'contain',
          filter: iconColor !== '#595959' && iconColor !== meta.color ?
            `brightness(0) saturate(100%) invert(1) sepia(100%) saturate(5000%) hue-rotate(${getHueFromColor(iconColor)}deg)` :
            'none'
        },
        onError: (e) => {
          e.target.style.display = 'none';
        }
      }
    );
  } else if (meta.iconComponent) {
    // Component иконка (lucide-react)
    const IconComponent = meta.iconComponent;
    return React.createElement(IconComponent, {
      'aria-hidden': true,
      className,
      size,
      color: iconColor,
      strokeWidth: 2,
      style: {
        display: 'inline-flex',
        flexShrink: 0,
        alignItems: 'center',
        justifyContent: 'center',
        ...style,
      },
    });
  }

  return null;
};

/**
 * Вспомогательная функция для получения hue из цвета (для фильтра изображений)
 */
const getHueFromColor = (hexColor) => {
  const r = parseInt(hexColor.slice(1, 3), 16) / 255;
  const g = parseInt(hexColor.slice(3, 5), 16) / 255;
  const b = parseInt(hexColor.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;

  if (max === min) {
    h = 0; // ахроматический
  } else if (max === r) {
    h = ((g - b) / (max - min) + (g < b ? 6 : 0)) / 6 * 360;
  } else if (max === g) {
    h = ((b - r) / (max - min) + 2) / 6 * 360;
  } else {
    h = ((r - g) / (max - min) + 4) / 6 * 360;
  }

  return Math.round(h);
};

/**
 * Принудительная перезагрузка стилей из API
 * @param {number|null} companyId - ID компании
 */
export const reloadResourceStyles = async (companyId = null) => {
  cachedStyles = null;
  cachedCompanyStyles = null;
  loadingPromise = null;
  companyStylesLoadingPromise = null;
  return await initStyles(companyId);
};

// НЕ инициализируем автоматическую загрузку при импорте
// Загрузка будет происходить только при первом вызове getResourceMeta()
// Это предотвращает ненужные запросы при импорте модуля

// Экспортируем дефолтные значения для совместимости
export const RESOURCE_META = DEFAULT_RESOURCE_META;

// ===================================================
// ЦВЕТА ДЛЯ БАЛАНСА И РАСХОДОВ
// ===================================================
export const BILLING_COLORS = {
  // Цвет для бесплатных ресурсов и стоимости услуг
  free: '#10B981', // DS --ok (light)
  // Цвет для платных ресурсов и расходов (то что берется)
  paid: '#F59E0B', // DS --warn (light)
  // Цвет для баланса (фирменный)
  balance: '#0284C7', // В тон DS --brand (light)
  // Цвет для недостатка баланса
  insufficient: '#EF4444' // DS --err (light)
};

// Получить цвет для бесплатных/стоимости услуг
export const getFreeColor = () => BILLING_COLORS.free;

// Получить цвет для платных/расходов
export const getPaidColor = () => BILLING_COLORS.paid;

// Получить цвет для баланса
export const getBalanceColor = () => BILLING_COLORS.balance;

// ===================================================
// ФИРМЕННЫЕ ЦВЕТА ТЕМЫ ПРИЛОЖЕНИЯ
// ===================================================
// Дефолтные цвета темы (fallback)
const DEFAULT_THEME_COLORS = {
  // Основной фирменный цвет (синхрон с markbase-design-system --brand light)
  primary: '#0284C7',
  // Сайдбар по умолчанию — классический тёмный rail (как Ant Design); пара с светлым текстом
  sidebarBackground: '#001529',
  // Фон шапки
  headerBackground: '#ffffff',
  // Фон основного контента
  contentBackground: '#f5f5f5',
  // Подсветка hover в превью настроек темы (10% от primary)
  sidebarHover: 'rgba(2, 132, 199, 0.1)',
  // Цвет выделенного пункта меню в сайдбаре
  sidebarSelected: '#0284C7',
  sidebarText: 'rgba(255, 255, 255, 0.85)',
  sidebarIcon: 'rgba(255, 255, 255, 0.65)',
  sidebarBorder: 'rgba(255, 255, 255, 0.12)',
  sidebarDivider: 'rgba(255, 255, 255, 0.12)',
  // Граница шапки
  headerBorder: '#f0f0f0',
  // Фон кнопок при наведении
  buttonHover: '#f5f5f5'
};

// Кэш загруженных цветов темы
let cachedThemeColors = null;
let themeLoadingPromise = null;

/**
 * Загрузка цветов темы из API.
 * С компанией: effective (платформа + overlay). Без — публичная платформа.
 */
const loadThemeColorsFromAPI = async (companyId = null) => {
  try {
    const api = getApi();
    if (!api || typeof api.get !== 'function') {
      console.warn('⚠️ API client not initialized, using default theme colors');
      cachedThemeColors = DEFAULT_THEME_COLORS;
      return DEFAULT_THEME_COLORS;
    }

    let apiColors = null;

    if (companyId) {
      try {
        const companyResponse = await api.get(`/api/settings/companies/${companyId}/theme-colors`);
        if (companyResponse?.data?.success && companyResponse.data.colors) {
          apiColors = companyResponse.data.colors;
        }
      } catch (e) {
        // fallback public
      }
    }

    if (!apiColors) {
      try {
        const globalResponse = await Promise.race([
          api.get('/api/admin/public/theme-colors'),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Theme colors request timeout')), 5000)
          ),
        ]);
        if (globalResponse?.data?.success && globalResponse.data.colors) {
          apiColors = globalResponse.data.colors;
        }
      } catch (error) {
        if (error.message?.includes('CORS') || error.message?.includes('Network Error') ||
            error.code === 'ERR_NETWORK' || error.response?.status === 403) {
          console.warn('⚠️ CORS or network error loading theme colors, using defaults:', error.message);
        } else if (error?.response?.status !== 404) {
          console.warn('⚠️ Не удалось загрузить цвета темы из API, используются значения по умолчанию:', error);
        }
        cachedThemeColors = DEFAULT_THEME_COLORS;
        return DEFAULT_THEME_COLORS;
      }
    }

    const mergedColors = {
      ...DEFAULT_THEME_COLORS,
      ...(apiColors || {}),
    };

    cachedThemeColors = mergedColors;
    return mergedColors;
  } catch (error) {
    console.warn('⚠️ Unexpected error loading theme colors, using defaults:', error);
  }

  cachedThemeColors = DEFAULT_THEME_COLORS;
  return DEFAULT_THEME_COLORS;
};

/**
 * Инициализация цветов темы
 * @param {number|null} companyId - ID компании для загрузки цветов темы компании
 */
const initThemeColors = async (companyId = null) => {
  // ✅ Защита от вызова до инициализации модуля и циклических зависимостей
  if (typeof DEFAULT_THEME_COLORS === 'undefined' || !DEFAULT_THEME_COLORS) {
    console.warn('⚠️ Theme colors not initialized, using fallback');
    return DEFAULT_THEME_COLORS || {};
  }

  // ✅ Защита от повторной инициализации во время загрузки
  if (cachedThemeColors && (!companyId || cachedThemeColors._companyId === companyId)) {
    return cachedThemeColors;
  }

  // ✅ Защита от параллельных запросов
  if (themeLoadingPromise) {
    try {
      return await themeLoadingPromise;
    } catch (e) {
      // Если предыдущий запрос упал, продолжаем с новым
      themeLoadingPromise = null;
    }
  }

  // ✅ Создаем новый промис с защитой от ошибок
  themeLoadingPromise = (async () => {
    try {
      const colors = await loadThemeColorsFromAPI(companyId);
      if (colors && typeof colors === 'object' && Object.keys(colors).length > 0) {
        colors._companyId = companyId;
        return colors;
      }
      return DEFAULT_THEME_COLORS;
    } catch (error) {
      console.warn('⚠️ Theme colors loading error:', error);
      return DEFAULT_THEME_COLORS;
    } finally {
      themeLoadingPromise = null;
    }
  })();

  return themeLoadingPromise;
};

/**
 * Получить цвет темы
 * @param {string} colorKey - Ключ цвета (primary, sidebarBackground, и т.д.)
 * @returns {string} Значение цвета
 */
export const getThemeColor = (colorKey) => {
  // Защита от вызова до инициализации модуля
  if (typeof DEFAULT_THEME_COLORS === 'undefined') {
    console.warn('⚠️ Theme colors not initialized, using fallback');
    return '#595959';
  }

  // Если цвета еще не загружены, возвращаем дефолтные значения синхронно
  if (!cachedThemeColors) {
    // Инициализируем асинхронно в фоне
    initThemeColors().catch((e) => reportResourceMetaSideEffectFailure('initThemeColors (getThemeColor)', e));
    return DEFAULT_THEME_COLORS[colorKey] || '#595959';
  }

  return cachedThemeColors[colorKey] || DEFAULT_THEME_COLORS[colorKey] || '#595959';
};

/**
 * Получить все цвета темы
 * @returns {Object} Все цвета темы
 */
export const getThemeColors = () => {
  // Защита от вызова до инициализации модуля
  if (typeof DEFAULT_THEME_COLORS === 'undefined') {
    console.warn('⚠️ Theme colors not initialized, using fallback');
    return {};
  }

  if (!cachedThemeColors) {
    // Инициализируем асинхронно в фоне, но возвращаем дефолтные значения синхронно
    initThemeColors().catch((e) => reportResourceMetaSideEffectFailure('initThemeColors (getThemeColors)', e));
    return DEFAULT_THEME_COLORS;
  }
  return cachedThemeColors;
};

/**
 * Принудительная перезагрузка цветов темы из API
 * @param {number|null} companyId - ID компании
 */
export const reloadThemeColors = async (companyId = null) => {
  cachedThemeColors = null;
  themeLoadingPromise = null;
  return await initThemeColors(companyId);
};

// НЕ инициализируем автоматическую загрузку цветов темы при импорте
// Загрузка будет происходить только при первом вызове getThemeColor() или getThemeColors()
// Это предотвращает ненужные запросы при импорте модуля

// Экспортируем дефолтные цвета для совместимости
export const THEME_COLORS = DEFAULT_THEME_COLORS;

